import { PCheckboxWrapper } from '@porsche-design-system/components-react';
import { type CheckboxProps } from 'antd';
import type { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { type ChangeEvent, useContext, useEffect, useMemo, useRef } from 'react';
import useFullLabelWidth from '../hooks/useFullLabelWidth';
import GroupContext from './Context';

const Checkbox = ({ onChange, onClick, children, checked, disabled, name, value, indeterminate }: CheckboxProps) => {
    const wrapper = useFullLabelWidth();

    const checkboxRef = useRef<HTMLInputElement>(null);

    const checkboxGroup = useContext(GroupContext);
    const prevValue = useRef(value);

    useEffect(() => {
        checkboxGroup?.registerValue(value);
    }, []);

    useEffect(() => {
        if (value !== prevValue.current) {
            checkboxGroup?.cancelValue(prevValue.current);
            checkboxGroup?.registerValue(value);
            prevValue.current = value;
        }

        return () => checkboxGroup?.cancelValue(value);
    }, [value]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
            onChange(e as unknown as CheckboxChangeEvent);
        }

        if (checkboxGroup?.toggleOption) {
            checkboxGroup?.toggleOption({ label: children, value });
        }
    };

    useEffect(() => {
        if (checkboxRef.current) {
            checkboxRef.current.indeterminate = indeterminate;
        }
    }, [indeterminate]);

    const derivedChecked = useMemo(() => {
        if (checkboxGroup) {
            return checkboxGroup?.value && checkboxGroup?.value.includes(value);
        }

        return checked;
    }, [checkboxGroup, checked, value]);

    return (
        <PCheckboxWrapper ref={wrapper}>
            {children && (
                <div slot="label" style={{ paddingRight: '8px' }}>
                    {children}
                </div>
            )}
            <input
                ref={checkboxRef}
                checked={derivedChecked}
                disabled={disabled}
                name={checkboxGroup?.name ?? name}
                onChange={handleChange}
                onClick={onClick}
                type="checkbox"
                value={value}
            />
        </PCheckboxWrapper>
    );
};

export default Checkbox;
